import { OTPInput, OTPInputContext } from 'input-otp';
import { Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

const PinInput = React.forwardRef<
  React.ComponentRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    className={cn('disabled:cursor-not-allowed', className)}
    containerClassName={cn(
      'flex items-center gap-2 has-[:disabled]:opacity-50',
      containerClassName,
    )}
    ref={ref}
    {...props}
  />
));

PinInput.displayName = 'PinInput';

const PinInputGroup = React.forwardRef<
  React.ComponentRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div className={cn('flex items-center', className)} ref={ref} {...props} />
));

PinInputGroup.displayName = 'PinInputGroup';

const PinInputSlot = React.forwardRef<
  React.ComponentRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
  const context = React.useContext(OTPInputContext);
  if (!context.slots[index]) return;
  const { char, hasFakeCaret, isActive } = context.slots[index];

  return (
    <div
      className={cn(
        'relative flex h-10 w-10 items-center justify-center border-input border-y border-e font-mono shadow-sm transition-all first:rounded-s-md first:border-s last:rounded-e-md',
        isActive && 'z-10 ring-1 ring-ring',
        className,
      )}
      ref={ref}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
});

PinInputSlot.displayName = 'PinInputSlot';

const PinInputSeparator = React.forwardRef<
  React.ComponentRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  // biome-ignore lint/a11y/useFocusableInteractive: pin-input separator is not interactive
  // biome-ignore lint/a11y/useAriaPropsForRole: pin-input separator is a separator
  <div ref={ref} role="separator" {...props}>
    <Minus className="h-5 w-5" />
  </div>
));

PinInputSeparator.displayName = 'PinInputSeparator';

export { PinInput, PinInputGroup, PinInputSeparator, PinInputSlot };
